import { dialog, message } from '@/utils/naive-instance'
import type { UploadCustomRequestOptions, UploadFileInfo } from 'naive-ui'
import { ref, type Ref } from 'vue'
import type { DisplayFile, UploadProps } from '../types'
import {
  convertToDisplayFile,
  formatMaxFileSize,
  getFileTypeDescription
} from '../utils'
export interface UploadInstance {
  internalFileList: Ref<UploadFileInfo[]>
  uploadProgress: Ref<number>
  showPreviewModal: Ref<boolean>
  previewModalFile: Ref<DisplayFile | null>
  getIsImageType: () => boolean
  getCurrentFile: () => DisplayFile | null
  getShowImagePreview: () => boolean
  getShowDocumentList: () => boolean
  getDocumentFiles: () => DisplayFile[]
  handleBeforeUpload: (options: { file: UploadFileInfo }) => Promise<boolean>
  handleCustomRequest: (options: UploadCustomRequestOptions) => Promise<void>
  handleRemove: (options: { file: UploadFileInfo }) => Promise<boolean>
  handleRemoveFile: (file: DisplayFile) => Promise<boolean>
  handlePreview: (file: DisplayFile) => void
  handleDownload: (file: DisplayFile) => void
  handleFinish: (options: { file: UploadFileInfo }) => void
  handleError: (options: { file: UploadFileInfo }) => void
  handleChange: (options: {
    file: UploadFileInfo
    fileList: UploadFileInfo[]
  }) => void
  handleClear: () => void
  triggerUpload: (defaultUploadRef: any) => void
}
export type UploadFileType = 'image' | 'file'
export function createUploadLogic(
  props: UploadProps,
  emit: any,
  fileStore: any
): UploadInstance {
  const internalFileList = ref<UploadFileInfo[]>([])
  const uploadProgress = ref(0)
  const showPreviewModal = ref(false)
  const previewModalFile = ref<DisplayFile | null>(null)

  // ========== 【BUG修复1：修复图片判断失效核心逻辑，优先级更正，百分百生效】 ==========
  const getIsImageType = (): boolean => {
    const accept = props.accept || ''
    // 优先级：props.uploadType 最高 > accept包含图片后缀/类型 > 默认false
    // 解决：图片上传也显示文档的核心问题
    if (props.uploadType === 'image') return true
    if (props.uploadType === 'file') return false
    const imageReg = /image\/|.jpg|.jpeg|.png|.gif|.webp/i
    return imageReg.test(accept)
  }

  const getCurrentFile = () => {
    // 优先取组件内部的上传文件列表，这是核心！
    if (internalFileList.value.length > 0) {
      return convertToDisplayFile(internalFileList.value[0])
    }
    // 内部无文件，再从store取值兜底
    const storeFile = fileStore.getFileByKey(props.fileKey)
    return storeFile ? convertToDisplayFileFromItem(storeFile) : null
  }

  const getShowImagePreview = () => {
    return (
      getIsImageType() &&
      !props.multiple &&
      (internalFileList.value.length === 1 ||
        fileStore.getFileByKey(props.fileKey))
    )
  }

  const getShowDocumentList = () => {
    // 互斥逻辑：不是图片类型 + 有文件，才显示文档列表，彻底区分图片/文档
    return !getIsImageType() && internalFileList.value.length > 0
  }

  const getDocumentFiles = () => {
    return internalFileList.value.map((file) => convertToDisplayFile(file))
  }

  const handleBeforeUpload = async (options: { file: UploadFileInfo }) => {
    const { file: fileInfo } = options
    if (!fileInfo.file) {
      message.warning('文件对象异常，请重新选择文件')
      return false
    }
    // ========== 【BUG修复3：移除错误的清空列表代码 internalFileList.value = [] ==========
    // 原逻辑：上传前清空列表，导致多文件上传时列表丢失，单文件也会出现选中不显示的问题
    if (props.maxFileSize && fileInfo.file.size > props.maxFileSize) {
      const maxSizeText = formatMaxFileSize(props.maxFileSize)
      message.error(`文件「${fileInfo.file.name}」大小不能超过${maxSizeText}`)
      return false
    }

    if (props.accept) {
      const acceptTypes = props.accept
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
      if (acceptTypes.length === 0) return true

      const fileType = fileInfo.file.type
      const fileName = fileInfo.file.name
      const fileExt = fileName.split('.').pop()?.toLowerCase() || ''

      const isValidType = acceptTypes.some((type) => {
        if (type.startsWith('.')) return type.toLowerCase() === `.${fileExt}`
        if (type.includes('/*'))
          return fileType?.startsWith(`${type.split('/')[0]}/`)
        return fileExt === type || type === fileType
      })

      if (!isValidType) {
        const acceptText = getFileTypeDescription(props.accept)
        message.error(
          `文件「${fileName}」格式不合法，请上传 ${acceptText} 格式的文件`
        )
        return false
      }
    }

    return props.onBeforeUpload?.(fileInfo) ?? true
  }

  const handleCustomRequest = async (options: UploadCustomRequestOptions) => {
    const { file, onFinish, onError, onProgress } = options
    try {
      const isValid = await handleBeforeUpload({ file })
      if (!isValid) {
        onError?.()
        return
      }

      uploadProgress.value = 0
      const progressInterval = setInterval(() => {
        if (uploadProgress.value < 90) {
          uploadProgress.value += 10
          onProgress?.({ percent: uploadProgress.value })
          emit('upload-progress', uploadProgress.value)
        }
      }, 200)

      const fileData = await fileStore.uploadFile(
        file.file as File,
        props.scene,
        props.fileKey
      )
      clearInterval(progressInterval)

      if (fileData) {
        uploadProgress.value = 100
        onProgress?.({ percent: 100 })

        const newFileInfo: UploadFileInfo = {
          ...file,
          url: fileData.url,
          id: fileData.id.toString(),
          thumbUrl: fileData.url,
          status: 'finished' as const,
          size: file.file?.size || 0,
          name: fileData.name || file.file?.name
        }

        if (props.multiple) {
          const isDuplicate = internalFileList.value.some(
            (f) => f.id === newFileInfo.id
          )
          if (!isDuplicate) {
            internalFileList.value = [...internalFileList.value, newFileInfo]
          }
        } else {
          internalFileList.value = [newFileInfo]
        }
        onFinish?.()
        updateModelValue(fileData)
        emit('success', fileData)
        props.onSuccess?.(fileData)
        message.success(`${file.name} 上传成功`)
        // fileStore.setFileByKey(props.fileKey, fileData)
      } else {
        onError?.()
        message.error(`${file.name} 上传失败`)
      }
    } catch (error) {
      console.error('上传失败:', error)
      onError?.()
      message.error(`${file.name} 上传失败`)
      internalFileList.value = internalFileList.value.filter(
        (f) => f.uid !== file.uid
      )
    }
  }

  const handleRemove = async (options: {
    file: UploadFileInfo
  }): Promise<boolean> => {
    const { file } = options
    return new Promise((resolve: (value: boolean) => void) => {
      dialog.warning({
        title: '确认删除',
        content: `确定要删除文件 "${file.name}" 吗？`,
        positiveText: '删除',
        negativeText: '取消',
        onPositiveClick: async () => {
          try {
            if (file.id && file.status === 'finished') {
              const success = await fileStore.deleteFile(
                file.id,
                props.fileKey,
                props.scene
              )
              if (!success) {
                message.error('文件删除失败')
                resolve(false)
                return
              }
            }

            internalFileList.value = props.multiple
              ? internalFileList.value.filter((f) => f.id !== file.id)
              : []
            removeFromModelValue(file.id)

            emit('remove', file)
            props.onRemove?.(file)
            if (!props.multiple) fileStore.clearFileByKey(props.fileKey)

            message.success('文件删除成功')
            resolve(true)
          } catch (error) {
            console.error('删除失败:', error)
            message.error('文件删除失败')
            resolve(false)
          }
        },
        onNegativeClick: () => resolve(false)
      })
    })
  }

  const handleRemoveFile = async (file: DisplayFile): Promise<boolean> => {
    const uploadFileInfo: UploadFileInfo = {
      id: file.id,
      name: file.name,
      status: 'finished' as const,
      url: file.url
    }
    return handleRemove({ file: uploadFileInfo })
  }

  const handlePreview = (file: DisplayFile) => {
    previewModalFile.value = file
    showPreviewModal.value = true
  }

  const handleDownload = (file: DisplayFile) => {
    if (file.url) {
      const a = document.createElement('a')
      a.href = file.url
      a.download = file.name || 'download'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      message.success('开始下载文件')
    } else {
      message.error('文件下载地址无效')
    }
  }

  const handleFinish = (options: { file: UploadFileInfo }) => {
    const { file } = options
    const fileData = fileStore.getFileByKey(props.fileKey)
    emit('finish', file, fileData)
    props.onFinish?.(file, fileData)
  }

  const handleError = (options: { file: UploadFileInfo }) => {
    const { file } = options
    emit('error', file)
    props.onError?.(file)
    message.error(`${file.name} 上传失败`)
  }

  const handleChange = (options: {
    file: UploadFileInfo
    fileList: UploadFileInfo[]
  }) => {
    emit('change', options.file, options.fileList)
    props.onChange?.(options.file, options.fileList)
  }

  const handleClear = () => {
    internalFileList.value = []
    fileStore.clearFileByKey(props.fileKey)
    emit('update:modelValue', props.multiple ? [] : null)
  }

  const triggerUpload = (defaultUploadRef: any) => {
    if (!defaultUploadRef) return
    if (defaultUploadRef.$el) {
      const input = defaultUploadRef.$el.querySelector('input[type="file"]')
      if (input) {
        input.click()
        return
      }
    }
    if (defaultUploadRef.triggerUpload) {
      defaultUploadRef.triggerUpload()
    }
    // 方式3：兜底 - 直接访问uploadInput（旧版本naive-ui）
    else if (defaultUploadRef.uploadInput) {
      defaultUploadRef.uploadInput.click()
    }
  }

  // ===== 内部辅助方法 =====
  const convertToDisplayFileFromItem = (fileItem: any): DisplayFile => {
    return {
      id: fileItem.id?.toString() || '',
      name: fileItem.name || '',
      url: fileItem.url || '',
      thumbUrl: fileItem.url,
      size: fileItem.size || fileItem.fileSize || 0,
      status: fileItem.status,
      type: fileItem.type?.toString()
    }
  }

  const updateModelValue = (fileData: any) => {
    if (props.multiple) {
      const currentValue = Array.isArray(props.modelValue)
        ? [...props.modelValue]
        : []
      const newValue = currentValue
        .filter((item) => item.id !== fileData.id)
        .concat(fileData)
      emit('update:modelValue', newValue)
    } else {
      const emitValue = props.isFileUrl
        ? fileData.url
        : props.isFileId
        ? fileData.id
        : fileData
      console.log(`output->emitValue`, emitValue)
      emit('update:modelValue', emitValue)
    }
  }

  const removeFromModelValue = (fileId: string) => {
    if (props.multiple && Array.isArray(props.modelValue)) {
      const newValue = props.modelValue.filter((item) => item.id !== fileId)
      emit('update:modelValue', newValue)
    } else {
      emit('update:modelValue', null)
    }
  }

  const loadFullFileList = () => {
    internalFileList.value = []
    const fileFromStore = fileStore.getFileByKey(props.fileKey)
    if (fileFromStore) {
      internalFileList.value = [
        {
          id: fileFromStore.id.toString(),
          name: fileFromStore.name || '未命名文件',
          status: 'finished' as const,
          url: fileFromStore.url || '',
          thumbUrl: fileFromStore.url || '',
          size: fileFromStore.size || fileFromStore.fileSize || 0,
          uid: `uid-file-${fileFromStore.id}-${Date.now()}`
        }
      ]
    }
  }
  // ========== 【BUG修复6：修复初始化执行时机，避免组件渲染时无数据】 ==========
  setTimeout(() => loadFullFileList(), 0)

  return {
    internalFileList,
    uploadProgress,
    showPreviewModal,
    previewModalFile,
    getIsImageType,
    getCurrentFile,
    getShowImagePreview,
    getShowDocumentList,
    getDocumentFiles,
    handleBeforeUpload,
    handleCustomRequest,
    handleRemove,
    handleRemoveFile,
    handlePreview,
    handleDownload,
    handleFinish,
    handleError,
    handleChange,
    handleClear,
    triggerUpload
  }
}
